/**
 * What to do when the sign-in library fails during boot.
 *
 * The console bricked itself. Its own stored MSAL state made every load throw
 * before the sign-in screen could render, the screen that appeared instead
 * said "The service is not answering", and the only way out was clearing the
 * browser's app data. Which means: a wrong message, pointing at the wrong
 * system, with no route back, and a remedy no operator would ever guess.
 *
 * Two ways it gets stuck, and both will happen again:
 *
 *   - an abandoned redirect. MSAL writes an interaction-in-progress marker
 *     when a login starts and clears it when the login returns. Close the tab,
 *     press back, or have the app registration reject the redirect URI, and
 *     the marker stays. Every later attempt throws on it, forever.
 *
 *   - a cached account whose refresh token has expired. Silent renewal throws,
 *     by design, because the answer really is "ask the person". That is the
 *     ordinary state of any operator who signs in today and returns tomorrow.
 *
 * Neither is an error. Both mean "not signed in", which is a screen this
 * console already has.
 */

/** MSAL owns keys under this prefix in both web storages. Nothing else is
 *  touched: clearing storage wholesale is what the browser's own button does,
 *  and it takes the theme, the remembered filters and anything added later
 *  with it. */
const MSAL_KEY_PREFIX = 'msal.'

export interface SignInFailure {
  /** Whether the boot may carry on and render the sign-in screen. It always
   *  may: a failed silent sign-in is not a failure of the service, and the
   *  person is standing right there. */
  recoverable: true
  /** Whether MSAL's stored state is the thing that is stuck, so it has to be
   *  dropped or the next attempt throws the same way. */
  clearCache: boolean
  /** For the log. Not for the screen: the screen shows the sign-in form,
   *  because that is what the person needs to do next. */
  reason: string
}

/**
 * A stuck interaction marker, which is the one that never recovers on its own.
 *
 * Matched on the error code MSAL sets rather than on the message, because the
 * message is prose that changes between releases. The name is checked too:
 * bundlers rename classes, so `instanceof BrowserAuthError` is not reliable
 * across a build.
 */
export function isStuckInteraction(err: unknown): boolean {
  const code = String(
    (err as { errorCode?: string })?.errorCode ??
      (err as { name?: string })?.name ??
      '',
  ).toLowerCase()
  const message = String((err as { message?: string })?.message ?? '').toLowerCase()
  return (
    code.includes('interaction_in_progress') ||
    message.includes('interaction_in_progress') ||
    message.includes('interaction is currently in progress')
  )
}

export function describeSignInFailure(err: unknown): SignInFailure {
  if (isStuckInteraction(err)) {
    return {
      recoverable: true,
      clearCache: true,
      reason:
        'a previous sign-in was never finished, so the library refused to ' +
        'start another. Its stored state was dropped.',
    }
  }
  return {
    recoverable: true,
    clearCache: false,
    reason:
      'no usable session was found without asking the person. That is the ' +
      'ordinary case, including a cached account whose token has expired.',
  }
}

/**
 * Drop MSAL's own keys, and only those.
 *
 * Takes the storages rather than reaching for globals, so the behaviour can be
 * asked directly rather than inferred from a browser that has been poked.
 */
export function clearStuckSignInState(
  storages: Array<Pick<Storage, 'key' | 'length' | 'removeItem'>>,
): string[] {
  const dropped: string[] = []
  for (const storage of storages) {
    const keys: string[] = []
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i)
      if (key && key.startsWith(MSAL_KEY_PREFIX)) keys.push(key)
    }
    for (const key of keys) {
      storage.removeItem(key)
      dropped.push(key)
    }
  }
  return dropped
}
